import React, {useMemo, useCallback} from 'react';
import {CircularProgress} from '@mui/material';
import {Dropdown, TextArea, TextInput, Toggle} from '@/components/atoms';
import {SectionTitle} from '@/components/molecules';
import ImagePicker from '@/containers/Rewards/RewardSetup/ui-components/ImagePicker';
// no store interactions needed here
// Component now relies on useRewards hook for state/logic; no local state needed
import styles from './_rewardsDisplay.module.scss';
import {useRewards} from '@/hooks/useRewards';
import LabelWithTooltip from '@/components/atoms/LabelWithTooltip/LabelWithTooltip';
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants';
import ToasterAlert from '@/components/atoms/Toast/toasterAlert';
import {COLOR_CODE} from '@/utils/constants/colors';

/**
 * RewardDisplay component - Step 2 of the reward creation process.
 * Handles image uploads and reward text for different languages.
 *
 * @returns {JSX.Element} The rendered RewardDisplay component.
 */
const RewardDisplay = () => {
  const {
    // data/state from hook
    languages,
    rewardDescription,
    productOptions,
    productChildList,
    completedCount,
    activeProductId,
    onProductChange,
    useDefaultImageToggleValue,
    handleUseDefaultImageToggleChange,
    isImageUploading,
    imagesForActiveProduct,
    onImageSelectAt,
    showToast,
    setShowToast,
    toastMessage,
    formData,
    updateGlobalRewardText,
    updateRewardText,
    imageUpload,
    displaySingleImageUpload,
    display
  } = useRewards();

  // Reward text source: only BONUS_PRODUCTS uses per-product text (productDisplay).
  // PRODUCT and PEP_VOUCHER_PRODUCT use global reward text (formData.rewardText) for
  // Display step validation (getStep2ValidationResult) and payload.
  const isProductBasedRewardType = useMemo(
    () => formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS,
    [formData?.rewardType]
  );
  const productRewardText = useMemo(
    () => display?.productDisplay?.[activeProductId]?.rewardText || {},
    [display?.productDisplay, activeProductId]
  );
  const currentRewardText = useMemo(
    () =>
      isProductBasedRewardType ? productRewardText : formData.rewardText || {},
    [isProductBasedRewardType, productRewardText, formData.rewardText]
  );
  const handleTextUpdate = useMemo(
    () =>
      isProductBasedRewardType ? updateRewardText : updateGlobalRewardText,
    [isProductBasedRewardType, updateRewardText, updateGlobalRewardText]
  );

  // Memoize image-related computations
  const imageComputations = useMemo(() => {
    const images = imagesForActiveProduct;
    const isSingle = !!displaySingleImageUpload;
    const filled = images.filter((img) => !!img.url);
    const singleImageSelect = imageUpload();
    // If using default images, render only existing filled images for their actual positions
    // Otherwise render sequential positions with one extra slot for upload (up to 3)
    const positionsToRender = (() => {
      if (isSingle) return [1];

      const filledLength = filled.length;

      if (useDefaultImageToggleValue) {
        return filled
          .slice()
          .sort((a, b) => Number(a?.position) - Number(b?.position))
          .map((img) => Number(img?.position));
      }

      const maxSlots = filledLength < 3 ? filledLength + 1 : filledLength;

      return Array.from({length: Math.min(3, maxSlots)}, (_, i) => i + 1);
    })();

    return {images, isSingle, filled, singleImageSelect, positionsToRender};
  }, [
    imagesForActiveProduct,
    displaySingleImageUpload,
    useDefaultImageToggleValue,
    imageUpload
  ]);

  // Memoize toast close handler
  const handleToastClose = useCallback(() => {
    setShowToast(!showToast);
  }, [showToast, setShowToast]);

  return (
    <div
      className={`flex flex-col gap-4 rounded-lg p-6 ${styles.rewardsDisplay} ${styles.rewardsDisplay__card}`}>
      <SectionTitle
        label={rewardDescription}
        color={COLOR_CODE._FFFFFF}
        className='w-full pb-0 pt-0'
      />

      <ToasterAlert
        type='warning'
        message={toastMessage}
        show={showToast}
        onCloseClick={handleToastClose}
      />

      <div className='border-t border-t-[#3A3A3A]'></div>
      {productChildList?.length > 1 && (
        <div className='mb-4 mt-2 flex flex-row items-end space-x-10'>
          <Dropdown
            className='dropdown'
            isJsonWrap={false}
            childList={productChildList}
            isDark={true}
            label='Product'
            placeholder={'Select Product'}
            selection='single'
            size='medium'
            style={{maxWidth: '425px'}}
            value={
              activeProductId
                ? [
                    {
                      id: activeProductId,
                      displayText: productOptions.find(
                        (p) => p.id === activeProductId
                      )?.displayText
                    }
                  ]
                : []
            }
            updateValue={onProductChange}
            showSelectedItems={true}
            isClearable={false}
          />
          <div className='mb-2 text-sm text-gray-50'>
            {completedCount.count}/{completedCount.total} completed
          </div>
        </div>
      )}

      {/* display / upload images section start */}
      <>
        {!imageComputations.isSingle && (
          <>
            <LabelWithTooltip
              className='text-[16px] font-bold text-white'
              label={'Reward Card'}
              showLabelIcon
              tooltipText='A minimum of 1 image is required. You may upload up to 3 images.'
            />

            <div className='mt-2'>
              <div className='flex items-center gap-3'>
                <Toggle
                  value={useDefaultImageToggleValue}
                  updateValue={handleUseDefaultImageToggleChange}
                  disabled={isImageUploading}
                  uiSchema={{
                    label: 'Use Default Image',
                    size: 'medium',
                    variant: 'horizontal',
                    isDark: true,
                    className: ''
                  }}
                />
                {isImageUploading && <CircularProgress size={16} />}
              </div>
            </div>
          </>
        )}

        {imageComputations.isSingle && (
          <LabelWithTooltip
            className='text-[16px] font-bold text-white'
            label={'Reward Card'}
          />
        )}

        <div
          className={
            imageComputations.isSingle ? 'my-4 flex flex-col' : 'mt-2'
          }>
          <div
            className={
              imageComputations.isSingle ? 'mt-2' : 'flex flex-wrap gap-6'
            }>
            {imageComputations.positionsToRender.map((pos) => (
              <div key={pos} style={{width: '380px'}}>
                {!imageComputations.isSingle && (
                  <h3 className='my-1 text-[16px] font-medium text-[color:var(--color-text-primary,#EFEFEF)]'>{`Display Position ${pos}`}</h3>
                )}

                <ImagePicker
                  key={`${imageComputations.isSingle ? 'single' : String(activeProductId || 'no-product')}-pos-${pos}-${String(formData?.rewardType || '')}`}
                  onImageSelect={
                    imageComputations.isSingle
                      ? imageComputations.singleImageSelect
                      : onImageSelectAt(pos)
                  }
                  value={
                    imageComputations.isSingle
                      ? formData.imageUrls?.[0]
                      : imageComputations.images.find(
                          (i) => Number(i?.position) === pos
                        )?.url
                  }
                  label={pos === 1 ? 'Reward Image*' : 'Reward Image'}
                  allowedMimeTypes={['image/jpeg', 'image/png']}
                  invalidFormatsMessage={
                    'File format is not valid. Supported Formats: PNG|JPG'
                  }
                  disableDelete={useDefaultImageToggleValue}
                  disabled={useDefaultImageToggleValue}
                />
              </div>
            ))}
          </div>
        </div>
      </>
      {/* display / upload images section end */}
      <div className='border-t border-t-[#3A3A3A]'></div>
      <SectionTitle
        label={'Reward Text'}
        color={COLOR_CODE._FFFFFF}
        className='w-full pb-0 pt-0'
      />

      <div className='flex flex-wrap gap-6'>
        {languages.map((lang) => {
          const languageName = lang.language;
          const language = lang.code;

          return (
            <div
              key={language}
              className='mt-0 space-y-5 font-semibold'
              style={{width: '380px'}}>
              <h3 className='text-[12px] font-bold text-[color:var(--color-text-primary,#EFEFEF)]'>
                {languageName}
              </h3>

              <TextInput
                key={`title-${activeProductId}-${language}`}
                showLabelIcon
                feedbackType='info'
                inputSize='medium'
                label='Title*'
                className='reward-display-text-input w-full text-[14px]'
                onChange={(value) => handleTextUpdate(language, {title: value})}
                onUpdate={(value) => handleTextUpdate(language, {title: value})}
                placeholderText={'Add Title'}
                value={currentRewardText?.[language]?.title || ''}
                autocomplete={language === 'es_MX' ? 'off' : 'on'}
              />

              <TextArea
                key={`description-${activeProductId}-${language}`}
                label='Description*'
                maxLength={500}
                trim={false}
                updateValue={(value) =>
                  handleTextUpdate(language, {description: value})
                }
                value={currentRewardText?.[language]?.description || ''}
                uiSchema={{
                  helperText: '',
                  placeholder: 'Type here',
                  showToolbar: false,
                  className: 'reward-display-text-input w-full',
                  showCharacterCount: true,
                  maxLength: 500
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

RewardDisplay.propTypes = {
  // This component doesn't accept props as it uses useRewards hook internally
  // PropTypes are defined here for documentation purposes but won't be enforced
};

export default React.memo(RewardDisplay);
